import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { CatalogFilters } from "../components/catalog-filters";
import { ProductCard } from "../components/product-card";
import { useCart } from "../hooks/use-cart";
import { fetchCatalog, fetchSearch, type CatalogResponse, type SearchResponse } from "../lib/catalog";

export function SearchPage() {
  const [params] = useSearchParams();
  const query = params.get("q") || "";
  const [catalogMeta, setCatalogMeta] = useState<CatalogResponse | null>(null);
  const [results, setResults] = useState<SearchResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { addOne, cartCounts } = useCart();

  useEffect(() => {
    let ignore = false;

    async function load() {
      setLoading(true);
      setError(null);

      try {
        const [catalog, search] = await Promise.all([
          fetchCatalog(new URLSearchParams()),
          query ? fetchSearch(params) : Promise.resolve({ query: "", results: [] })
        ]);

        if (!ignore) {
          setCatalogMeta(catalog);
          setResults(search);
        }
      } catch {
        if (!ignore) {
          setError("Unable to load search results right now.");
        }
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    }

    void load();
    return () => {
      ignore = true;
    };
  }, [params, query]);

  return (
    <section className="mx-auto w-full max-w-[92rem] px-3 py-12 sm:px-4 lg:px-5">
      <div className="grid gap-6 lg:grid-cols-[280px_minmax(0,1fr)]">
        <aside className="glass-panel rounded-[28px] p-5 lg:sticky lg:top-24 lg:self-start">
          <p className="eyebrow">Search filters</p>
          <h2 className="mt-3 text-2xl font-semibold">Precision controls</h2>
          {catalogMeta ? (
            <CatalogFilters
              basePath="/shop/search"
              categories={catalogMeta.categories}
              initialQuery={query}
              initialCategory={params.get("category") || ""}
              initialInStock={params.get("in_stock") === "true"}
              initialMax={params.get("max") || ""}
              initialMin={params.get("min") || ""}
              initialSort="featured"
            />
          ) : null}
        </aside>
        <div className="glass-panel rounded-[28px] p-6">
          <p className="text-sm uppercase tracking-[0.2em] text-nv-slate">Search shell</p>
          <h1 className="mt-3 text-3xl font-semibold tracking-tight">
            {query ? `Results for "${query}"` : "Search the catalog"}
          </h1>
          {loading ? (
            <div className="mt-8 rounded-[24px] border border-dashed border-black/10 bg-white/60 p-6 text-sm text-nv-slate">
              Searching catalog...
            </div>
          ) : error ? (
            <div className="mt-8 rounded-[24px] border border-red-200 bg-red-50 p-6 text-sm text-red-700">
              {error}
            </div>
          ) : !query ? (
            <div className="mt-8 rounded-[24px] border border-dashed border-black/10 bg-white/60 p-6 text-sm text-nv-slate">
              Start with a part number, board name, module type, or application keyword.
            </div>
          ) : results && results.results.length > 0 ? (
            <div className="mt-8 grid gap-5 md:grid-cols-2 xl:grid-cols-3">
              {results.results.map((item) => (
                <ProductCard
                  cartQuantity={cartCounts.get(item.id) || 0}
                  category={item.category || "Uncategorized"}
                  description={item.description || "No description yet."}
                  image={item.image}
                  id={item.id}
                  key={item.id}
                  name={item.name}
                  price={item.price}
                  quantity={item.quantity}
                  slug={item.slug}
                  onAddToCart={addOne}
                />
              ))}
            </div>
          ) : (
            <div className="mt-8 rounded-[24px] border border-dashed border-black/10 bg-white/60 p-6 text-sm text-nv-slate">
              No results matched this search. Try a broader term, remove some filters, or search by module family.
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
